import { ThemeKey } from "@smartsheet/design-tokens";
import { genId } from "@smartsheet/lodestar-utils";
import React, { forwardRef, useMemo, useRef } from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { CONTACT_PICKER_CLASS } from "../contactpicker/contactPickerConstants";
import { SELECT_TOGGLE_ELEMENT_CLASS } from "../selectV2/constants";
import { VisuallyHidden } from "../visuallyhidden";
import { FormLabelChild } from "./styled/FormLabelChild";
import { FormLabelContentAfter } from "./styled/FormLabelContentAfter";
import { FormLabelDescription } from "./styled/FormLabelDescription";
import { FormLabelFeedback } from "./styled/FormLabelFeedback";
import { FormLabelIcon } from "./styled/FormLabelIcon";
import { FormLabelLabel, labelVariantProps } from "./styled/FormLabelLabel";
import { FormLabelStyled } from "./styled/FormLabelStyled";
import { FormLabelValidation } from "./styled/FormLabelValidation";
import { descriptionVariantProps } from "./styled/formLabelColorTheme";
import { iconBasedOnValidationState } from "./styled/formLabelIcons";
import { FormLabelChildProps, FormLabelProps } from "./types";

export const FORM_LABEL_CLICK_EVENT_ID = 1212512; //the string "label" in A=1, B=2 ...

export const FormLabel = forwardRef<HTMLElement, FormLabelProps>(
    (
        {
            children,
            id,
            label: labelText,
            labelProps: providedLabelProps,
            description,
            descriptionProps: providedDescriptionProps,
            validationRootProps,
            hideLabel,
            validationState,
            validationProps: providedValidationProps,
            validationMessage,
            icon,
            clientId,
            contentAfter,
            "aria-describedby": ariaDescribedBy,
            placeholderId,
            ...rest
        },
        ref
    ) => {
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const childId = useMemo(() => id ?? genId("fl-input"), [id]);
        const formLabelChildRef = useRef<HTMLDivElement | null>(null);

        const providedLabelId = providedLabelProps?.id;
        const labelId = useMemo(() => providedLabelId ?? genId("fl-label"), [providedLabelId]);
        const finalLabelProps = {
            htmlFor: childId,
            ...providedLabelProps,
            id: labelId,
            validationState: validationState ?? "info",
        } as const;

        const providedDescriptionId = providedDescriptionProps?.id;
        const descId = useMemo(() => providedDescriptionId ?? genId("fl-desc"), [providedDescriptionId]);
        const finalDescriptionProps = {
            ...providedDescriptionProps,
            tooltipPlacement: providedDescriptionProps?.tooltipPlacement ?? "bottom",
            id: descId,
        } as const;

        const childProps: FormLabelChildProps = {
            id: childId,
            "aria-labelledby": placeholderId ? `${labelId} ${placeholderId}` : labelId, // enables more descriptive datepicker accessible label text
            ...(description && { "aria-describedby": `${descId} ${ariaDescribedBy ?? ""}`.trim() }),
        };

        const handleLabelClick = (e: React.MouseEvent<HTMLDivElement | HTMLLabelElement>) => {
            if (providedLabelProps?.onClick) {
                providedLabelProps.onClick(e as React.MouseEvent<HTMLLabelElement>);
            }
            if (formLabelChildRef.current) {
                //dispatch a new click event with the same properties as the original event
                //but with a distinct detail key to identify it as a label click
                const newEvent = new MouseEvent("click", {
                    ...e,
                    view: window,
                    detail: FORM_LABEL_CLICK_EVENT_ID,
                });
                // The contactPicker uses the SelectV2 under the hood meaning it has the TOGGLE_ELEMENT_CLASS
                // we want to make sure we don't dispatch the new event in ContactPicker cases
                // because the default behavior of highlighting the input is wanted.
                // SelectV2 are different in that they should open their menus on label click.
                const isAContactPicker = formLabelChildRef.current.querySelector(`.${CONTACT_PICKER_CLASS}`);
                const childTarget = formLabelChildRef.current.querySelector(`.${SELECT_TOGGLE_ELEMENT_CLASS}`);
                if (!isAContactPicker && childTarget) {
                    //prevent the previous event from being triggered
                    e.preventDefault();
                    childTarget.dispatchEvent(newEvent);
                }
            }
        };

        const label = (
            <FormLabelLabel
                renderAs="label"
                {...finalLabelProps}
                variant={labelVariantProps(themeMode) as "bodyStrong" | "Inter--12px--600w"}
                themeMode={themeMode}
                onClick={handleLabelClick}
            >
                {labelText}
                {(contentAfter && <FormLabelContentAfter>{contentAfter}</FormLabelContentAfter>) || null}
            </FormLabelLabel>
        );

        return (
            <FormLabelStyled data-client-id={clientId} ref={ref as React.ForwardedRef<HTMLDivElement>} {...rest}>
                {hideLabel ? <VisuallyHidden>{label}</VisuallyHidden> : label}
                <FormLabelDescription
                    {...finalDescriptionProps}
                    variant={descriptionVariantProps(themeMode) as "bodyText" | "Inter--12px--400w"}
                    themeMode={themeMode}
                    onClick={handleLabelClick}
                >
                    {description}
                </FormLabelDescription>
                <FormLabelChild ref={formLabelChildRef}>{children(childProps)}</FormLabelChild>
                <FormLabelFeedback
                    orientation="row"
                    space="xxSmall"
                    aria-live="assertive"
                    themeMode={themeMode}
                    role="alert"
                    aria-busy={validationState === "waiting"}
                    {...validationRootProps}
                >
                    {validationMessage && validationState && (
                        <>
                            <FormLabelIcon validationState={validationState || "info"} themeMode={themeMode}>
                                {icon ?? iconBasedOnValidationState(validationState, themeMode)}
                            </FormLabelIcon>
                            <FormLabelValidation
                                validationState={validationState}
                                {...providedValidationProps}
                                // @ts-expect-error variant type is complaining
                                variant={descriptionVariantProps(themeMode)}
                                themeMode={themeMode}
                            >
                                {validationMessage}
                            </FormLabelValidation>
                        </>
                    )}
                </FormLabelFeedback>
            </FormLabelStyled>
        );
    }
);

FormLabel.displayName = "FormLabel";
